import { Facebook, Instagram, LinkedIn, Search, Twitter } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useEffect, useRef, useState, useMemo } from 'react';
import { FiEdit } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { IProviedServicesViewModel } from 'viewModels';
import { v4 as uuid } from 'uuid';
import { useAuth, useRegisterForm } from '../../context';
import useClickOutside from '../../hooks/useClickOutside';
import {
  ClassificationType,
  CorrespondenceType,
  TypeUser,
  labelCorrespondenceType,
} from '../../types/enums';
import { Button } from '../Buttons/Button';
import { Container } from './styles';
import ImageBronze from '../../assets/images/DiamondBronze.png';
import ImageGold from '../../assets/images/DiamondGold.png';
import ImageSilver from '../../assets/images/DiamondSilver.png';
import { SolicitationController } from '../../controller/SolicitationController';
import { useDistricts } from '../../context/districts';
import { UF_BY_CODE } from '../../constants';
import UserMap from '../UserMap';
import { ModalSearchCollaborators } from '../Modals/ModalSearchCollaborators';

export function FeedProfileInformation() {
  const { presentationLetter, address, linkFacebook, linkInstagram, linkTwitter, linkLinkedIn } =
    useRegisterForm();
  const { user, tokenIsValid } = useAuth();
  const { districtsSelectedsByUser } = useDistricts();
  const [displayActMap, setDisplayActMap] = useState(false);
  const [openModalSearchCorrespondent, setOpenModalSearchCorrespondent] = useState<boolean>(false);
  const ref = useRef(null);
  const [proviedServices, setProviedServices] = useState<IProviedServicesViewModel[]>([]);
  useClickOutside(() => setDisplayActMap(false), ref);

  const districtsUfAcronyms = useMemo(() => {
    const districts = districtsSelectedsByUser?.map((d) => UF_BY_CODE[d.ufCode]);
    return districts.filter((d, index) => districts.indexOf(d) === index);
  }, [districtsSelectedsByUser]);

  const districtsUfs = useMemo(() => {
    const districts = districtsSelectedsByUser?.map((d) => d.uf);
    return districts.filter((d, index) => districts.indexOf(d) === index);
  }, [districtsSelectedsByUser]);

  useEffect(() => {
    if (tokenIsValid) {
      SolicitationController.getNumberProviedServices(user.id).then((res) => {
        setProviedServices(res);
      });
    }
  }, [tokenIsValid, user.id]);

  return (
    <Container>
      <div className="header">
        <div className="local">
          <h1>{address?.city ? `De ${address?.city}` : 'Local não informado'}</h1>
        </div>
        <Link
          to={
            [TypeUser.COMPANY, TypeUser.ADM].includes(user.type)
              ? '/legalPersonAccount'
              : '/naturalPersonAccount'
          }>
          <FiEdit />
        </Link>
      </div>
      <div className="containerAct" ref={ref}>
        <Button
          text="Onde Atua?"
          dimensions={{
            width: '5rem',
            height: '2rem',
          }}
          onClick={() => setDisplayActMap(true)}
          background="transparent"
          color="primary"
          textHoverColor="secondary"
        />

        {displayActMap && (
          <div className="map">
            <div className="header">
              <button type="button" onClick={() => setDisplayActMap(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <UserMap districts={districtsUfAcronyms} />
            <div>
              {districtsUfs &&
                districtsUfs.map((uf) => (
                  <div key={uf} className="containerStates">
                    <h1>{uf}</h1>
                    <div className="containerCities">
                      {districtsSelectedsByUser
                        .filter((d) => d.uf === uf)
                        .map((district) => (
                          <p key={district.id}>{district.name},</p>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      {user.type === TypeUser.USER && (
        <div className="service">
          <div className="classificationDiamond">
            {user.classification === ClassificationType.SILVER && <img src={ImageSilver} alt="" />}
            {user.classification === ClassificationType.BRONZE && <img src={ImageBronze} alt="" />}
            {user.classification === ClassificationType.GOLD && <img src={ImageGold} alt="" />}
          </div>
          {proviedServices.length > 0 && <h1>Realizou</h1>}
          <div className="allService">
            {proviedServices.map((service, index) => (
              <div key={service.numberProviedServices + uuid()}>
                {service.typeUser !== TypeUser.ADM && (
                  <div key={service.numberProviedServices}>
                    <text className="numberServices">{service.numberProviedServices}</text>
                    <text className="labelCorrespondence">
                      {labelCorrespondenceType[service.type as CorrespondenceType]}
                    </text>
                    {index === proviedServices.length - 1 ? '.' : ','}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="summary">
        {presentationLetter && (
          <>
            <h1>Apresentação</h1>
            <div className="containerPresentation">
              <p>{presentationLetter}</p>
            </div>
          </>
        )}
      </div>
      <div className="socialMedias">
        {linkFacebook && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => window.open(`https://www.facebook.com/${linkFacebook}`, '_blank')}>
            <Facebook />
          </IconButton>
        )}
        {linkInstagram && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => window.open(`https://www.instagram.com/${linkInstagram}`, '_blank')}>
            <Instagram />
          </IconButton>
        )}
        {linkTwitter && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => window.open(`https://www.twitter.com/${linkTwitter}`, '_blank')}>
            <Twitter />
          </IconButton>
        )}
        {linkLinkedIn && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => window.open(`https://www.linkedin.com/${linkLinkedIn}`, '_blank')}>
            <LinkedIn />
          </IconButton>
        )}
      </div>
      <Button
        onClick={() => setOpenModalSearchCorrespondent(true)}
        text="Procure um correspondente"
        dimensions={{
          width: '19rem',
          height: '2.5rem',
        }}
        font={{ weight: 500 }}
        style={{
          marginTop: '4rem',
        }}
        background="tertiary_500"
        color="background_white"
        border={{ color: 'tertiary_500', width: 1 }}
        textHoverColor="background_white"
        LeftComponent={Search}
        positionLeftElement={15}
        backgroundHoverColor="tertiary_500"
      />
      {openModalSearchCorrespondent && (
        <ModalSearchCollaborators
          isOpen={openModalSearchCorrespondent}
          onClose={() => setOpenModalSearchCorrespondent(false)}
        />
      )}
    </Container>
  );
}
